import type { ComponentType } from "react";
import { toSourceTypeId } from "@onetasklist/shared/sources/source-settings";

export interface SourceAuthSetupProps {
  /** The source this instance is configuring — lets components shared across sources (e.g. CdsAuthSetup) brand themselves correctly. */
  sourceId: string;
  /** Called whenever the setup values change — parent threads these into oauthStart. */
  onSetupChange: (setup: Record<string, string>, isValid: boolean) => void;
  /**
   * When provided, the source auth setup component can trigger the connect/auth
   * action itself (e.g. VSTS renders its own "Continue with Microsoft" button).
   */
  onConnect?: () => Promise<void>;
  isConnecting?: boolean;
  /** When rendered in dialog mode, called to close the dialog without navigating away. */
  onDismiss?: () => void;
}

const registry = new Map<string, ComponentType<SourceAuthSetupProps>>();
const ownsAuthButtonsSet = new Set<string>();
const usesDialogSet = new Set<string>();

export const registerSourceAuthSetup = (
  sourceId: string,
  component: ComponentType<SourceAuthSetupProps>,
  ownsAuthButtons = false,
  usesDialog = false,
): void => {
  registry.set(sourceId.toLowerCase(), component);
  if (ownsAuthButtons) ownsAuthButtonsSet.add(sourceId.toLowerCase());
  if (usesDialog) usesDialogSet.add(sourceId.toLowerCase());
};

export const getSourceAuthSetup = (
  sourceId: string,
): ComponentType<SourceAuthSetupProps> | null => {
  const normalizedId = toSourceTypeId(sourceId) ?? sourceId.toLowerCase();
  return registry.get(normalizedId) ?? null;
};

/** True when the source's auth setup component renders its own connect buttons. */
export const sourceOwnsAuthButtons = (sourceId: string): boolean => {
  const normalizedId = toSourceTypeId(sourceId) ?? sourceId.toLowerCase();
  return ownsAuthButtonsSet.has(normalizedId);
};

/**
 * True when the source's auth setup component should be shown in a dialog
 * triggered by the Connect button, rather than rendered inline in the form.
 */
export const sourceAuthSetupUsesDialog = (sourceId: string): boolean => {
  const normalizedId = toSourceTypeId(sourceId) ?? sourceId.toLowerCase();
  return usesDialogSet.has(normalizedId);
};
